import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAppKit } from '@reown/appkit/react';
import { useAccount, useSendTransaction, useChainId, useSwitchChain } from 'wagmi';
import { parseEther } from 'viem';

// --- NEOMORPHISM TOKENS ---
const NEO = {
  bg: '#ffffff',
  shadow: '8px 8px 16px rgba(163,177,198,0.4), -8px -8px 16px rgba(255,255,255,1)',
  shadowHover: '4px 4px 8px rgba(163,177,198,0.5), -4px -4px 8px rgba(255,255,255,1)',
  shadowInner: 'inset 4px 4px 8px rgba(163,177,198,0.4), inset -4px -4px 8px rgba(255,255,255,1)',
  textMain: '#2B3674',
  textMuted: '#A3AED0',
  accent: '#7C3AED',
  accentGrad: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
  border: 'rgba(255,255,255,0.7)',
  green: '#05CD99',
};

// --- DATA ---
const NODES = [
  { id: 'tier-1', name: '1x RTX 4090 Node', fullPrice: 1000, dailyYield: 100000, tag: 'Starter' },
  { id: 'tier-2', name: '2x RTX 4090 Cluster', fullPrice: 2500, dailyYield: 262500 },
  { id: 'tier-3', name: '4x RTX 4090 Array', fullPrice: 5000, dailyYield: 550000 },
  { id: 'tier-4', name: '1x A100 Training Node', fullPrice: 10000, dailyYield: 1150000, tag: 'Popular' },
  { id: 'tier-5', name: '2x A100 Training Node', fullPrice: 20000, dailyYield: 2400000 },
  { id: 'tier-6', name: '4x A100 Training Cluster', fullPrice: 30000, dailyYield: 3750000 },
  { id: 'tier-7', name: '8x A100 SuperCluster', fullPrice: 40000, dailyYield: 5200000, tag: 'Pro' },
  { id: 'tier-8', name: '1x H100 Enterprise Node', fullPrice: 50000, dailyYield: 6750000 },
  { id: 'tier-9', name: '2x H100 Enterprise Array', fullPrice: 75000, dailyYield: 10500000 },
  { id: 'tier-10', name: '4x H100 Enterprise SuperPod', fullPrice: 100000, dailyYield: 15000000, tag: 'Enterprise' },
  { id: 'tier-demo', name: 'Demo Node', fullPrice: 5, dailyYield: 50, tag: 'Test' }
];

export default function SentinelHost() {
  const [selectedNode, setSelectedNode] = useState(NODES[0]);
  const [paymentMethod, setPaymentMethod] = useState('fiat'); // 'fiat', 'crypto', 'dgcc'
  const [cryptoNetwork, setCryptoNetwork] = useState('erc20'); // 'erc20', 'bep20'
  const [isProcessing, setIsProcessing] = useState(false);
  const { open } = useAppKit();
  const { isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { sendTransactionAsync } = useSendTransaction();

  const currentPrice = selectedNode.fullPrice;

  return (
    <div style={{ background: NEO.bg, minHeight: '100%', padding: '24px', fontFamily: '"Inter", sans-serif', color: NEO.textMain }}>
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .tier-accordion-item {
          position: relative;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.5s cubic-bezier(0.05, 0.61, 0.41, 0.95);
          background-size: 200% 200%;
          animation: gradientShift 10s ease infinite;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }
        .tier-accordion-item.active {
          flex-grow: 1000;
          min-width: 340px;
          border-radius: 32px;
          margin: 0;
        }
        .tier-accordion-item.inactive {
          flex-grow: 1;
          min-width: 60px;
          max-width: 60px;
          border-radius: 24px;
          opacity: 0.8;
          margin: 0;
        }
        .tier-accordion-item.inactive:hover {
          opacity: 1;
        }
        
        .tier-content-expanded {
          opacity: 0;
          transform: translateX(20px);
          transition: all 0.4s ease 0.1s;
          width: max-content;
        }
        .tier-accordion-item.active .tier-content-expanded {
          opacity: 1;
          transform: translateX(0);
        }
      `}</style>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.02em' }}>Host A GPU Node</h1>
          <p style={{ margin: 0, color: NEO.textMuted, fontSize: '14px' }}>Fund decentralized AI infrastructure and earn daily yield</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>

        {/* --- LEFT MAIN CONTENT --- */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Node Accordion */}
          <div className="tier-accordion-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', height: '500px', gap: '12px', width: '100%', overflowX: 'auto', paddingBottom: '12px' }}>
            {NODES.map((node, index) => {
              const isSelected = selectedNode.id === node.id;
              
              const SILVER = 'linear-gradient(135deg, #E0E0E0 0%, #F5F5F5 20%, #8E9EAB 50%, #F5F5F5 80%, #E0E0E0 100%)';
              const BRONZE = 'linear-gradient(135deg, #CD7F32 0%, #FFCC99 20%, #8C5212 50%, #FFCC99 80%, #CD7F32 100%)';
              const GOLD   = 'linear-gradient(135deg, #BF953F 0%, #FCF6BA 20%, #B38728 50%, #FBF5B7 80%, #AA771C 100%)';

              const TIER_GRADIENTS = [
                SILVER, SILVER, SILVER, SILVER,
                BRONZE, BRONZE, BRONZE, BRONZE,
                GOLD, GOLD
              ];

              return (
                <div
                  key={node.id}
                  onClick={() => setSelectedNode(node)}
                  className={`tier-accordion-item ${isSelected ? 'active' : 'inactive'}`}
                  style={{
                    backgroundImage: TIER_GRADIENTS[index % TIER_GRADIENTS.length]
                  }}
                >
                  {/* Vertical text for inactive state */}
                  <div style={{ 
                    position: 'absolute', 
                    bottom: '35px', 
                    left: '50%', 
                    transform: 'translateX(-50%) rotate(-90deg)', 
                    transformOrigin: 'center center',
                    whiteSpace: 'nowrap',
                    opacity: isSelected ? 0 : 1,
                    transition: 'all 0.5s', 
                    zIndex: 2,
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(10px)',
                    padding: '8px 20px',
                    borderRadius: '30px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', letterSpacing: '2px' }}>TIER {index + 1}</span>
                  </div>

                  {/* Expanded Content */}
                  <div className="tier-content-expanded" style={{ padding: '32px', paddingLeft: '40px', paddingBottom: '16px', color: '#1f2937', height: '100%', display: 'flex', flexDirection: 'column', pointerEvents: isSelected ? 'auto' : 'none' }}>
                     <h2 style={{ fontSize: '32px', fontWeight: 900, marginBottom: '8px', letterSpacing: '-0.02em', color: '#000', whiteSpace: 'nowrap' }}>{node.name}</h2>
                     {node.tag && <div style={{ display: 'inline-block', background: 'rgba(0,0,0,0.8)', color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 700, marginBottom: 'auto', alignSelf: 'flex-start' }}>{node.tag}</div>}
                     
                     <div style={{ marginTop: 'auto', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(10px)', padding: '20px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.6)', minWidth: '220px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, opacity: 0.7 }}>Investment Price</div>
                        <div style={{ fontSize: '40px', fontWeight: 900, marginBottom: '16px' }}>${node.fullPrice.toLocaleString()}</div>
                        
                        <div style={{ fontSize: '14px', fontWeight: 700, opacity: 0.7 }}>Daily Yield</div>
                        <div style={{ fontSize: '20px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px', color: '#7C3AED', whiteSpace: 'nowrap' }}>
                           {node.dailyYield.toLocaleString()} Credits
                        </div>
                     </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>

        {/* --- RIGHT SIDEBAR --- */}
        <div style={{ width: '360px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '32px', position: 'sticky', top: '24px' }}>

          <div style={{ background: NEO.bg, borderRadius: '24px', padding: '32px', boxShadow: NEO.shadow }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: NEO.textMain }}>Investment Summary</div>
            </div>

            <div style={{ background: NEO.bg, borderRadius: '16px', padding: '20px', boxShadow: NEO.shadowInner, marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: NEO.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: `0 4px 10px rgba(124,58,237,0.4)` }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>
                </div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: NEO.textMain }}>{selectedNode.name}</div>
              </div>
              <div style={{ fontSize: '13px', color: NEO.textMuted, fontWeight: 500, lineHeight: 1.5 }}>
                Compute Package
              </div>
            </div>

            <div style={{ fontSize: '13px', color: NEO.textMuted, marginBottom: '8px' }}>Total Price</div>
            <div style={{ fontSize: '32px', fontWeight: 800, color: NEO.textMain, marginBottom: '24px', paddingBottom: '24px', borderBottom: `1px solid ${NEO.border}` }}>
              ${currentPrice.toLocaleString()} <span style={{ fontSize: '14px', fontWeight: 600 }}>USD</span>
            </div>

            <div style={{ fontSize: '14px', fontWeight: 700, color: NEO.textMain, marginBottom: '16px' }}>Estimated Yield</div>
            
            <div style={{ background: 'rgba(124,58,237,0.05)', borderRadius: '12px', padding: '16px', border: `1px solid rgba(124,58,237,0.2)`, marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: NEO.textMuted, fontWeight: 600, whiteSpace: 'nowrap' }}>Daily Payout</span>
                <span style={{ fontSize: '15px', fontWeight: 800, color: NEO.accent, whiteSpace: 'nowrap' }}>
                  {selectedNode.dailyYield.toLocaleString()} Credits
                </span>
              </div>
              <div style={{ fontSize: '11px', color: NEO.textMuted, lineHeight: 1.4 }}>
                Yield is generated daily in Credits.
              </div>
            </div>

            <motion.button
              disabled={isProcessing}
              onClick={async () => {
                setIsProcessing(true);
                try {
                  const token = localStorage.getItem('daggpt_token') || localStorage.getItem('token');
                  const headers = { 'Content-Type': 'application/json' };
                  if (token) headers['Authorization'] = `Bearer ${token}`;
                  else headers['x-user-id'] = 'demo_user';
                  
                  if (paymentMethod === 'fiat') {
                    const res = await fetch('/api/billing/create-custom-checkout', { method: 'POST', headers, body: JSON.stringify({ amountUsd: currentPrice }) });
                    const data = await res.json();
                    if (data.success && data.checkoutUrl) window.open(data.checkoutUrl, '_blank');
                    else alert(data.error || 'Failed to initialize Stripe checkout');
                  } else if (paymentMethod === 'crypto') {
                    const res = await fetch('/api/billing/create-nowpayments-checkout', { method: 'POST', headers, body: JSON.stringify({ amountUsd: currentPrice, cryptoNetwork }) });
                    const data = await res.json();
                    if (data.success && data.invoiceUrl) window.open(data.invoiceUrl, '_blank');
                    else alert(data.error || 'Failed to initialize Crypto checkout');
                  } else {
                     if (!isConnected) {
                       setIsProcessing(false);
                       return open();
                     }
                     if (chainId !== 707070) {
                       try {
                         await switchChainAsync({ chainId: 707070 });
                       } catch (e) {
                         setIsProcessing(false);
                         return alert('Please switch to DAG Chain to pay with DGCC.');
                       }
                     }
                     const treasuryAddress = '0xe34899de6cd7c85e2e353db866c53a9e082be6b5';
                     const txHash = await sendTransactionAsync({
                       to: treasuryAddress,
                       value: parseEther(currentPrice.toString())
                     });
                     const verifyRes = await fetch('/api/billing/verify-dgcc-node-purchase', {
                       method: 'POST', 
                       headers, 
                       body: JSON.stringify({ txHash, expectedAmount: currentPrice })
                     });
                     const verifyData = await verifyRes.json();
                     if (verifyData.success) {
                       window.location.href = '/dashboard?section=investor';
                     } else {
                       alert(verifyData.error || 'Failed to verify transaction.');
                     }
                  }
                } catch (err) {
                  alert('Network error initializing payment gateway.');
                } finally {
                  setIsProcessing(false);
                }
              }}
              whileHover={{ scale: isProcessing ? 1 : 1.02 }}
              whileTap={{ scale: isProcessing ? 1 : 0.98 }}
              style={{ width: '100%', padding: '16px', borderRadius: '12px', background: NEO.accentGrad, color: '#fff', border: 'none', fontSize: '16px', fontWeight: 700, cursor: isProcessing ? 'wait' : 'pointer', boxShadow: `0 10px 20px -5px rgba(124,58,237,0.5)`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: isProcessing ? 0.7 : 1 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
              {isProcessing ? 'Processing...' : paymentMethod === 'fiat' ? 'Fund with Stripe' : paymentMethod === 'crypto' ? 'Pay with Crypto' : 'Pay with DGCC'}
            </motion.button>

          </div>

          <div style={{ background: NEO.bg, borderRadius: '24px', padding: '32px', boxShadow: NEO.shadow }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: NEO.textMain, margin: '0 0 16px' }}>Payment Method</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

              <div onClick={() => setPaymentMethod('fiat')} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', borderRadius: '16px', background: NEO.bg, boxShadow: paymentMethod === 'fiat' ? NEO.shadowInner : 'none', border: `1px solid ${paymentMethod === 'fiat' ? NEO.accent : NEO.border}`, cursor: 'pointer' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: NEO.bg, boxShadow: NEO.shadow, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: NEO.textMain }}>Fiat (Stripe)</div>
                  <div style={{ fontSize: '12px', color: NEO.textMuted }}>Credit / Debit Card</div>
                </div>
              </div>

              <div onClick={() => setPaymentMethod('crypto')} style={{ display: 'flex', flexDirection: 'column', padding: '16px', borderRadius: '16px', background: NEO.bg, boxShadow: paymentMethod === 'crypto' ? NEO.shadowInner : 'none', border: `1px solid ${paymentMethod === 'crypto' ? NEO.accent : NEO.border}`, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: NEO.bg, boxShadow: NEO.shadow, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 18V6"/></svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: NEO.textMain }}>Crypto (NowPayments)</div>
                    <div style={{ fontSize: '12px', color: NEO.textMuted }}>Pay with 100+ Cryptocurrencies</div>
                  </div>
                </div>
                {paymentMethod === 'crypto' && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${NEO.border}`, display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
                    {['erc20', 'bep20'].map(net => (
                      <div 
                        key={net} 
                        onClick={() => setCryptoNetwork(net)}
                        style={{ flex: 1, textAlign: 'center', padding: '8px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, background: cryptoNetwork === net ? NEO.accent : 'transparent', color: cryptoNetwork === net ? '#fff' : NEO.textMain, border: `1px solid ${cryptoNetwork === net ? NEO.accent : NEO.border}`, cursor: 'pointer', transition: 'all 0.2s' }}
                      >
                        USDT {net.toUpperCase()}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div onClick={() => setPaymentMethod('dgcc')} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', borderRadius: '16px', background: NEO.bg, boxShadow: paymentMethod === 'dgcc' ? NEO.shadowInner : 'none', border: `1px solid ${paymentMethod === 'dgcc' ? NEO.accent : NEO.border}`, cursor: 'pointer' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: NEO.bg, boxShadow: NEO.shadow, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l10 6.5v7L12 22 2 15.5v-7L12 2z"/><line x1="12" y1="22" x2="12" y2="9"/><polyline points="2 8.5 12 15 22 8.5"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: NEO.textMain }}>DGCC Token</div>
                  <div style={{ fontSize: '12px', color: NEO.textMuted }}>Pay using connected Web3 Wallet</div>
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
